import { useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import toast from "react-hot-toast"
import nProgress from "nprogress"

import { lang } from "config"
import { useAuth } from "contexts/auth-context"
import Input from "components/forms/input"
import Label from "components/forms/label"
import Description from "components/forms/description"
import ErrorMessage from "components/forms/error-message"

function AsyncProductUnitPicker({
  disabled,
  outlet_group_id,
  outlet_id,
  value,
  onChange,
}) {
  const inventoryApi = process.env.REACT_APP_INVENTORY_API_URL
  const perPage = 30

  const [query, setQuery] = useState("")
  const [page, setPage] = useState(1)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const debounceRef = useRef()

  const buildUrl = (params) => {
    const url = new URL(`${inventoryApi}/ps_list`)
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== "") {
        url.searchParams.set(key, val)
      }
    })
    return url.toString()
  }

  const fetchPage = async (targetPage = 1, append = false) => {
    if (!inventoryApi || !outlet_id) return

    setLoading(true)
    try {
      const response = await fetch(
        buildUrl({
          page: targetPage,
          per_page: perPage,
          search: query,
          outlet_group_id,
          outlet_id,
          only_in_stock: true,
          with_attributes: false,
        })
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const json = await response.json()
      const rows = Array.isArray(json?.data) ? json.data : []

      setItems((prev) => (append ? [...prev, ...rows] : rows))
      setHasMore(Boolean(json?.meta?.next_page_url) || rows.length === perPage)
      setPage(targetPage)
    } catch {
      toast.error("Gagal mengambil daftar produk.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!outlet_id) {
      setItems([])
      return
    }

    fetchPage(1, false)
  }, [outlet_id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchPage(1, false)
    }, 300)

    return () => clearTimeout(debounceRef.current)
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  const onScroll = (event) => {
    const el = event.currentTarget
    if (!loading && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      fetchPage(page + 1, true)
    }
  }

  if (disabled || !outlet_id) {
    return <Input disabled value="Pilih outlet asal dulu" id="product_unit_picker_disabled" />
  }

  return (
    <div className="space-y-2">
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Cari SKU / Nama / Brand / Model..."
      />

      <div className="h-72 overflow-auto border rounded-xl" onScroll={onScroll}>
        {items.map((row) => {
          const id = row.product_unit_id ?? row.id
          const name = row.product_unit_long_name ?? row.product_unit ?? row.name ?? "-"
          const sku = row.sku ?? "-"
          const brand = row.product_brand ?? row.brand ?? "-"
          const stock = Number(row.current_stock ?? row.stock ?? 0)
          const isSelected = String(value?.product_unit_id || "") === String(id)

          return (
            <button
              key={id}
              type="button"
              className={`w-full px-3 py-2 text-left border-b last:border-b-0 hover:bg-neutral-50 ${isSelected ? "bg-neutral-100" : ""}`}
              onClick={() =>
                onChange({
                  product_unit_id: String(id),
                  product_unit_name: name,
                  sku,
                  brand,
                  available_stock: stock,
                })
              }
            >
              <div className="text-sm font-medium text-neutral-900">{name}</div>
              <div className="text-xs text-neutral-500">
                {sku} • {brand}
              </div>
              <div className="mt-1 text-[11px] text-green-700">Stock: {stock}</div>
            </button>
          )
        })}

        {loading && <div className="p-3 text-xs text-neutral-500">Memuat...</div>}
        {!loading && items.length === 0 && <div className="p-3 text-xs text-neutral-500">Tidak ada hasil</div>}
      </div>
    </div>
  )
}

export default function Form({ data, errors = {}, onSubmit }) {
  const genericApi = process.env.REACT_APP_GENERIC_API_URL
  const { isSuper, currentBranch } = useAuth()
  const isBranchLocked = !isSuper() && !!currentBranch?.id

  const [fromOutletId, setFromOutletId] = useState(
    isBranchLocked ? String(currentBranch.id) : (data?.from_outlet_id ?? "")
  )
  const [toOutletId, setToOutletId] = useState(data?.to_outlet_id ?? "")
  const [deliveryOrderNo, setDeliveryOrderNo] = useState(data?.delivery_order_no ?? "")
  const [branches, setBranches] = useState([])
  const [loadingBranches, setLoadingBranches] = useState(false)

  const [draftProduct, setDraftProduct] = useState(null)
  const [draftQty, setDraftQty] = useState("")
  const [items, setItems] = useState(() => {
    const initialItems = Array.isArray(data?.items) ? data.items : []
    return initialItems.map((item, index) => ({
      line_key: item.line_key || `existing-${index + 1}`,
      product_unit_id: String(item.product_unit_id),
      product_unit_name: item.product_unit_name || item.name || item.product_unit || item.product_unit_id,
      qty: Number(item.qty ?? item.qty_requested ?? 0),
      available_stock: Number(item.available_stock ?? item.current_stock ?? 0),
      sku: item.sku || "-",
      brand: item.brand || item.product_brand || "-",
    }))
  })

  const fetchBranches = async () => {
    setLoadingBranches(true)
    try {
      const response = await axios.get(`${genericApi}/branch`)
      const list = response?.data?.data ?? response?.data?.branches ?? response?.data ?? []
      setBranches(Array.isArray(list.data) ? list.data : [])
    } catch {
      toast.error("Gagal memuat outlet")
    } finally {
      setLoadingBranches(false)
    }
  }

  useEffect(() => {
    fetchBranches()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setItems([])
    setDraftProduct(null)
    setDraftQty("")
  }, [fromOutletId])

  const branchOptionLabel = (branch) => branch?.name ?? branch?.branch_name ?? branch?.code ?? branch?.id ?? ""
  const branchOptionValue = (branch) => branch?.id ?? branch?.branch_id ?? branch?.code ?? ""

  const findBranchNameById = (id) => {
    const branch = branches.find((row) => String(branchOptionValue(row)) === String(id))
    return branch ? branchOptionLabel(branch) : `OUTLET ${id}`
  }

  const totalQty = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.qty || 0), 0),
    [items]
  )

  const addDraftProduct = () => {
    if (!draftProduct?.product_unit_id) {
      toast.error("Pilih produk terlebih dahulu")
      return
    }

    const qty = Number(draftQty || 0)
    if (!qty || qty < 1) {
      toast.error("Qty produk harus lebih besar dari 0")
      return
    }

    if (qty > Number(draftProduct.available_stock || 0)) {
      toast.error(`Qty ${draftProduct.product_unit_name} melebihi stok tersedia`)
      return
    }

    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => String(item.product_unit_id) === String(draftProduct.product_unit_id)
      )

      if (existingIndex >= 0) {
        const next = [...prev]
        const nextQty = Number(next[existingIndex].qty || 0) + qty
        if (nextQty > Number(next[existingIndex].available_stock || 0)) {
          toast.error(`Total qty ${draftProduct.product_unit_name} melebihi stok tersedia`)
          return prev
        }

        next[existingIndex] = {
          ...next[existingIndex],
          qty: nextQty,
        }
        return next
      }

      return [
        ...prev,
        {
          line_key: `${draftProduct.product_unit_id}-${Date.now()}`,
          ...draftProduct,
          qty,
        },
      ]
    })

    setDraftProduct(null)
    setDraftQty("")
  }

  const updateItemQty = (lineKey, rawQty) => {
    const qty = Number(rawQty || 0)

    setItems((prev) =>
      prev.map((item) => {
        if (item.line_key !== lineKey) {
          return item
        }

        const cappedQty = qty > Number(item.available_stock || 0)
          ? Number(item.available_stock || 0)
          : qty

        return {
          ...item,
          qty: cappedQty < 0 ? 0 : cappedQty,
        }
      })
    )
  }

  const removeItem = (lineKey) => {
    setItems((prev) => prev.filter((item) => item.line_key !== lineKey))
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (nProgress.isStarted()) {
      toast.error("Previous action is loading")
      return
    }

    if (!fromOutletId || !toOutletId) {
      toast.error("Lengkapi semua field yang wajib")
      return
    }

    if (String(fromOutletId) === String(toOutletId)) {
      toast.error("Outlet asal dan tujuan tidak boleh sama")
      return
    }

    if (!items.length) {
      toast.error("Minimal satu produk harus dipilih")
      return
    }

    const invalidQty = items.find(
      (item) => Number(item.qty || 0) < 1 || Number(item.qty || 0) > Number(item.available_stock || 0)
    )

    if (invalidQty) {
      toast.error(`Qty ${invalidQty.product_unit_name} tidak valid`)
      return
    }

    onSubmit({
      from_outlet_id: fromOutletId,
      to_outlet_id: toOutletId,
      from_outlet_name: findBranchNameById(fromOutletId),
      to_outlet_name: findBranchNameById(toOutletId),
      delivery_order_no: deliveryOrderNo.trim(),
      items: items.map((item) => ({
        product_unit_id: item.product_unit_id,
        qty: Number(item.qty),
      })),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-8">
      <div className="mt-10 sm:mt-0">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">{lang.main_form}</h3>
              <p className="mt-1 text-sm text-gray-600">{lang.please_fill_the_provided_form_input}</p>
            </div>
          </div>

          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 sm:p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="from_outlet_id" value={`Outlet Asal * ${loadingBranches ? '(memuat...)' : ''}`} />
                    <select
                      id="from_outlet_id"
                      className="w-full h-10 px-3 border rounded-xl bg-transparent disabled:bg-neutral-100"
                      value={fromOutletId}
                      onChange={(event) => setFromOutletId(event.target.value)}
                      disabled={isBranchLocked}
                    >
                      <option value="">— Pilih Outlet Asal —</option>
                      {branches
                        .filter((branch) => !isBranchLocked || String(branchOptionValue(branch)) === String(currentBranch.id))
                        .map((branch) => {
                          const value = branchOptionValue(branch)
                          return (
                            <option key={`from-${value}`} value={value}>
                              {branchOptionLabel(branch)}
                            </option>
                          )
                        })}
                    </select>
                    <ErrorMessage error={errors.from_outlet_id} />
                  </div>

                  <div>
                    <Label htmlFor="to_outlet_id" value="Outlet Tujuan *" />
                    <select
                      id="to_outlet_id"
                      className="w-full h-10 px-3 border rounded-xl bg-transparent"
                      value={toOutletId}
                      onChange={(event) => setToOutletId(event.target.value)}
                    >
                      <option value="">— Pilih Outlet Tujuan —</option>
                      {branches
                        .filter((branch) => String(branchOptionValue(branch)) !== String(fromOutletId))
                        .map((branch) => {
                          const value = branchOptionValue(branch)
                          return (
                            <option key={`to-${value}`} value={value}>
                              {branchOptionLabel(branch)}
                            </option>
                          )
                        })}
                    </select>
                    <ErrorMessage error={errors.to_outlet_id} />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="delivery_order_no" value="Delivery Order No" />
                    <Input
                      id="delivery_order_no"
                      value={deliveryOrderNo}
                      onChange={(event) => setDeliveryOrderNo(event.target.value)}
                      placeholder="Kosongkan untuk auto-generate"
                      error={errors.delivery_order_no}
                    />
                    <Description value="Kosongkan jika ingin nomor DO dibuat otomatis saat draft disimpan." />
                  </div>
                </div>

                <div className="border rounded-xl p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label value="Pilih Product *" />
                      <AsyncProductUnitPicker
                        disabled={!fromOutletId}
                        value={draftProduct}
                        outlet_group_id={data?.outlet_group_id}
                        outlet_id={fromOutletId}
                        onChange={setDraftProduct}
                      />
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="draft_qty" value="Qty *" />
                        <Input
                          id="draft_qty"
                          type="number"
                          min={1}
                          max={Number(draftProduct?.available_stock || 0) || undefined}
                          value={draftQty}
                          onChange={(event) => setDraftQty(event.target.value)}
                          placeholder="0"
                        />
                        {draftProduct && (
                          <Description
                            value={`Stok tersedia: ${Number(draftProduct.available_stock || 0).toLocaleString("id-ID")}`}
                          />
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={addDraftProduct}
                        className="px-4 py-2 text-sm font-medium text-white bg-neutral-900 rounded-xl"
                      >
                        Tambah Product
                      </button>
                    </div>
                  </div>

                  <div className="overflow-hidden border rounded-xl">
                    <table className="min-w-full text-sm">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Product</th>
                          <th className="px-3 py-2 text-left">SKU</th>
                          <th className="px-3 py-2 text-right">Stock</th>
                          <th className="px-3 py-2 text-right">Qty *</th>
                          <th className="px-3 py-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-center text-neutral-500">
                              Belum ada produk dipilih.
                            </td>
                          </tr>
                        )}

                        {items.map((item) => (
                          <tr key={item.line_key} className="border-t">
                            <td className="px-3 py-3">
                              <div className="font-medium text-neutral-900">{item.product_unit_name}</div>
                              <div className="text-xs text-neutral-500">{item.brand}</div>
                            </td>
                            <td className="px-3 py-3 text-neutral-600">{item.sku}</td>
                            <td className="px-3 py-3 text-right text-green-700 font-medium">
                              {Number(item.available_stock || 0).toLocaleString("id-ID")}
                            </td>
                            <td className="px-3 py-3">
                              <input
                                type="number"
                                min={1}
                                max={Number(item.available_stock || 0)}
                                value={item.qty}
                                onChange={(event) => updateItemQty(item.line_key, event.target.value)}
                                className="w-28 px-3 py-2 text-right border rounded-lg"
                              />
                            </td>
                            <td className="px-3 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => removeItem(item.line_key)}
                                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg"
                              >
                                Hapus
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {items.length > 0 && (
                        <tfoot className="bg-neutral-50 border-t">
                          <tr>
                            <td colSpan={3} className="px-3 py-2 text-right font-semibold">Total Qty</td>
                            <td className="px-3 py-2 text-right font-semibold">{totalQty.toLocaleString("id-ID")}</td>
                            <td />
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>

                  <ErrorMessage error={errors.items} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-xs text-right">
        <button
          type="submit"
          className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90"
        >
          <span>{lang.create}</span>
        </button>
      </div>
    </form>
  )
}
